import React, { useEffect, useState } from 'react';

import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Grid,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MainGrid from '../../components/MainGrid';
import Dropzone from 'react-dropzone';
import { getElderHomeId } from '../../Utils/AuthUtil';
import {
	deleteTemplate,
	getPlanTemplate,
	getSummarizerTemplate,
	storePlanTemplate,
	storeSummaryTemplate,
} from '../../services/CarePlanServices';
import { toast } from 'react-toastify';
import { helperText } from '../../assets/theme/Theme';
import { DeleteOutline } from '@mui/icons-material';
import ToastBanner from '../../components/ToastBanner';
import { dark, teal } from '../../assets/theme/Colors';

const tableHeadStyle = {
	fontSize: '11px',
	fontWeight: 600,
	color: '#64748b',
	borderBottom: '1px solid #e2e8f0',
};

const labelStyle = {
	fontSize: '12px',
	fontWeight: 500,
	color: '#6b8888',
	mb: 0.8,
};

const inputStyle = {
	'& .MuiOutlinedInput-root': {
		borderRadius: '12px',
		fontSize: '13px',
		backgroundColor: '#fff',
	},
};

const actionBtn = {
	minWidth: 34,
	width: 34,
	height: 34,
	borderRadius: '10px',
	borderColor: '#dbe3ec',
	color: '#475569',
	p: 0,
};

const templates = [
	{
		name: 'Standard Care Plan',
		type: 'Care Plan',
		version: 'v2.1',
		color: '#14b8a6',
		bg: '#ccfbf1',
	},
	{
		name: 'Monthly Overview',
		type: 'Summary',
		version: 'v1.3',
		color: '#2563eb',
		bg: '#dbeafe',
	},
	{
		name: 'Wellbeing Report',
		type: 'Summary',
		version: 'v1.0',
		color: '#2563eb',
		bg: '#dbeafe',
	},
	{
		name: 'CQC Compliance',
		type: 'CQC',
		version: 'v1.0',
		color: '#d97706',
		bg: '#fef3c7',
	},
];

const TABS = {
	ptc: 'Plan',
	stc: 'Summarizer',
	cqc: 'CQC Report',
};

const SUMMARY_SCOPE = {
	og: 'Organization',
	re: 'Resident',
};

export default function TemplateConfiguration() {
	const homeId = getElderHomeId();
	const [txt, setTxt] = useState();
	const [fileData, setFileData] = useState(null);
	const [txtError, setTxtError] = useState({ status: false, msg: '' });
	const [loading, setLoading] = useState(false);
	const [planTemplates, setPlanTemplates] = useState([]);
	const [name, setName] = useState('');
	const [nameError, setNameError] = useState({ status: false, msg: '' });
	const [planData, setPlanData] = useState([]);
	const [selectedViewPlan, setSelectedViewPlan] = useState('');
	const [selectedViewPlanType, setSelectedViewPlanType] = useState('plan');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [summaryEndpoints, setSummaryEndpoints] = useState('');
	const [summaryEndpointsError, setSummaryEndpointsError] = useState({
		status: false,
		msg: '',
	});
	const [summaryType, setSummaryType] = useState(TABS.ptc);
	const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);
	const [summaryTempScope, setSummaryTempScope] = useState(SUMMARY_SCOPE.og);

	useEffect(() => {
		fetchTemplateFiles();
	}, []);

	async function fetchTemplateFiles() {
		setIsFetchingTemplates(true);
		const fetchedTemplates = [];
		const plResponse = await getPlanTemplate(homeId);

		if (!plResponse.error) {
			const plans = plResponse.templates.map(item => ({
				...item,
				type: 'Care Plan',
				color: '#14b8a6',
				bg: '#ccfbf1',
			}));
			fetchedTemplates.push(...plans);
		}

		const excludeTemp = [
			'narrative_summary',
			'structured_summary',
			'instruction_based_summary',
		];

		const clsResponse = await getSummarizerTemplate(homeId, 'elder');

		if (!clsResponse.error) {
			const temp = clsResponse.templates
				.filter(item => !excludeTemp.includes(item.template_key))
				.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
				.map(item => {
					return {
						template_name: item.name,
						template_id: item.id,
						content: item.content,
						endpoints: item.selected_endpoints_summariser,
						type: 'Resident Summary',
						color: '#2563eb',
						bg: '#dbeafe',
					};
				});
			fetchedTemplates.push(...temp);
		}

		const orsResponse = await getSummarizerTemplate(homeId, 'client');
		if (!orsResponse.error) {
			const temp = orsResponse.templates
				.filter(item => !excludeTemp.includes(item.template_key))
				.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
				.map(item => {
					return {
						template_name: item.name,
						template_id: item.id,
						content: item.content,
						endpoints: item.selected_endpoints_summariser,
						type: 'Organization Summary',
						color: '#2563eb',
						bg: '#dbeafe',
					};
				});
			fetchedTemplates.push(...temp);
		}

		setPlanTemplates(fetchedTemplates);
		setIsFetchingTemplates(false);
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0] && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData(e[0]);
	}

	function onRemoveFiles() {
		setFileData(null);
	}

	function validateInputs() {
		let isValid = true;
		if (!name || name === '') {
			setNameError({ status: true, msg: 'Enter template name' });
			isValid = false;
		}

		if (!fileData) {
			setTxtError({ status: true, msg: 'Upload the template files' });
			isValid = false;
		}

		return isValid;
	}

	async function onUploadPlanTemplate() {
		if (!validateInputs()) {
			return;
		}

		setLoading(true);
		const formData = new FormData();
		formData.append('template_file', fileData);
		formData.append('client_id', getElderHomeId());
		formData.append('template_name', name);

		const response = await storePlanTemplate(formData);

		if (!response.error) {
			toast.success('Plan template stored', {
				style: {
					background: '#4F9E4E',
				},
			});
			setFileData(null);
			setTxt(null);
			setName('');
			setSummaryType(TABS.ptc);
			fetchTemplateFiles();
		} else {
			toast.error(response.message);
		}

		setLoading(false);
	}

	async function onUploadSummaryTemplate() {
		if (!validateInputs()) {
			return;
		}

		setLoading(true);
		const formData = new FormData();
		formData.append('summary_template_file', fileData);
		formData.append('client_id', getElderHomeId());
		formData.append('name', name);
		formData.append('template_key', name);
		const endpoints = summaryEndpoints.split(',');
		endpoints.forEach(url => {
			formData.append('selected_endpoints_summariser', url);
		});
		formData.append(
			'template_scope',
			summaryTempScope === SUMMARY_SCOPE.og ? 'client' : 'elder',
		);
		const response = await storeSummaryTemplate(formData);

		if (!response.error) {
			toast.success('Summarizer template stored', {
				style: {
					background: '#4F9E4E',
				},
			});
			setFileData(null);
			setTxt(null);
			setName('');
			setSummaryEndpoints('');
			fetchTemplateFiles();
		} else {
			toast.error(response.message);
		}

		setLoading(false);
	}

	async function deleteSelectedTemplate(id, type) {
		const route =
			type === 'Care Plan'
				? `clients/${homeId}/care-templates/${id}`
				: `summary_templates/${id}`;

		const response = await deleteTemplate(route);
		if (!response.error) {
			toast.success('Template removed successfully', {
				style: {
					background: '#4F9E4E',
				},
			});
			setFileData(null);
			setTxt(null);
			const filterData = planTemplates.filter(
				item => item.template_id !== id,
			);
			setPlanTemplates(filterData);
		} else {
			toast.error(response.message);
		}
	}

	async function onSelect(item) {
		setTxt(null);
		setFileData(null);
		setTxtError({ status: false, msg: '' });
		setLoading(false);
		setPlanTemplates([]);
		setName('');
		setNameError({ status: false, msg: '' });
		setSummaryEndpointsError({ status: false, msg: '' });
	}

	function onViewTemplate(id, type) {
		if (type === 'Summarizer') {
			const item = planTemplates.find(item => item.template_id === id);
			setSelectedViewPlan(item.template_name);
			setPlanData(item);
			setSelectedViewPlanType('summary');
		} else {
			const item = planTemplates.find(item => item.template_id === id);
			const plan =
				item.template_config?.care_plan_templates?.initial?.sections;

			setSelectedViewPlan(item.template_name);
			setPlanData(plan);
			setSelectedViewPlanType('plan');
		}
		setIsModalOpen(true);
	}

	const cleanTitle = title => {
		return title.replace(/^\d+\.\s*/, ''); // Removes "14. " or "1. "
	};

	return (
		<MainGrid>
			<Box width={'100%'}>
				{/* Header */}
				<Box mb={3}>
					<Typography
						sx={{
							fontSize: '22px',
							fontWeight: 600,
							color: dark,
							mb: 0.5,
						}}>
						Template Configuration
					</Typography>

					<Typography
						sx={{
							fontSize: '14px',
							color: '#64748b',
						}}>
						Configure care plan and summarizer templates to match
						your organization&apos;s standards
					</Typography>
				</Box>

				<Grid container width="100%" spacing={1}>
					{/* LEFT */}
					<Grid item xs={12} md={7}>
						<Card
							sx={{
								borderRadius: '20px',
								border: '1px solid #e2e8f0',
								boxShadow: 'none',
							}}>
							<CardContent sx={{ p: 3 }}>
								{/* Title */}
								<Stack
									direction="row"
									spacing={1.2}
									alignItems="center"
									mb={2.5}>
									<DescriptionOutlinedIcon
										sx={{
											fontSize: 16,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 600,
											color: '#0f172a',
										}}>
										Current Templates
									</Typography>
								</Stack>

								{/* Table */}
								<TableContainer
									sx={{
										maxHeight: 420,
										overflowY: 'auto',
										borderRadius: '12px',
										border: '1px solid #f1f5f9',
										scrollbarWidth: 'none', // Firefox
										msOverflowStyle: 'none', // IE & Edge
										'&::-webkit-scrollbar': {
											display: 'none', // Chrome/Safari
										},
									}}>
									<Table stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell sx={tableHeadStyle}>
													Template Name
												</TableCell>

												<TableCell sx={tableHeadStyle}>
													Type
												</TableCell>

												<TableCell sx={tableHeadStyle}>
													Version
												</TableCell>

												<TableCell sx={tableHeadStyle}>
													Actions
												</TableCell>
											</TableRow>
										</TableHead>

										<TableBody>
											{planTemplates.length > 0 ? (
												planTemplates.map(
													(template, index) => (
														<TableRow
															key={index}
															sx={{
																'& td': {
																	borderBottom:
																		'1px solid #f1f5f9',
																},
															}}>
															<TableCell>
																<Typography
																	sx={{
																		fontSize:
																			'13px',
																		color: '#0f172a',
																	}}>
																	{
																		template.template_name
																	}
																</Typography>
															</TableCell>

															<TableCell>
																<Chip
																	label={
																		template.type
																	}
																	size="small"
																	sx={{
																		height: 26,
																		borderRadius:
																			'999px',
																		backgroundColor:
																			template.bg,
																		color: template.color,
																		fontWeight: 500,
																		fontSize:
																			'11px',
																	}}
																/>
															</TableCell>

															<TableCell>
																<Typography
																	sx={{
																		fontSize:
																			'13px',
																		color: '#334155',
																	}}>
																	v1.0
																</Typography>
															</TableCell>

															<TableCell>
																<Stack
																	direction="row"
																	spacing={1}>
																	<Button
																		onClick={() =>
																			deleteSelectedTemplate(
																				template.template_id,
																				template.type,
																			)
																		}
																		variant="outlined"
																		size="small"
																		sx={
																			actionBtn
																		}>
																		<DeleteOutline
																			sx={{
																				fontSize: 16,
																			}}
																		/>
																	</Button>
																</Stack>
															</TableCell>
														</TableRow>
													),
												)
											) : (
												<TableRow>
													<TableCell
														colSpan={4}
														align="center">
														<Typography
															sx={{
																fontSize: 13,
															}}>
															No data available
														</Typography>
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</CardContent>
						</Card>
					</Grid>

					{/* RIGHT */}
					<Grid item xs={12} md={5}>
						<Card
							sx={{
								borderRadius: '20px',
								border: '1px solid #e2e8f0',
								boxShadow: 'none',
							}}>
							<CardContent sx={{ p: 3 }}>
								{/* Title */}
								<Stack
									direction="row"
									spacing={1.2}
									alignItems="center"
									mb={2.5}>
									<UploadFileOutlinedIcon
										sx={{
											fontSize: 16,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 600,
											color: '#0f172a',
										}}>
										Upload Custom Template
									</Typography>
								</Stack>

								{/* Upload Area */}
								<Dropzone
									onDrop={acceptedFiles =>
										readTextFile(acceptedFiles)
									}>
									{({
										getRootProps,
										getInputProps,
										isDragActive,
									}) => (
										<Box
											{...getRootProps()}
											sx={{
												border: `2px dashed ${txtError.status ? 'red' : '#b3ede8'}`,
												borderRadius: '12px',
												p: '28px 20px',
												textAlign: 'center',
												background: isDragActive
													? '#d0f5f1'
													: '#e6f9f7',
												cursor: 'pointer',
												transition: 'all 0.15s ease',
												'&:hover': {
													background: '#d0f5f1',
													borderColor: teal,
												},
											}}>
											<input {...getInputProps()} />

											<UploadFileIcon
												sx={{
													fontSize: 32,
													color: teal,
													mb: 1,
												}}
											/>
											{!fileData ? (
												<>
													<Typography
														sx={{
															fontSize: 14,
															fontWeight: 600,
															color: dark,
															mb: 0.5,
														}}>
														Upload JSON template
														file
													</Typography>

													<Typography
														sx={{
															fontSize: 12,
															color: '#6b8888',
															lineHeight: 1.5,
														}}>
														Supports care plan and
														summarizer formats
													</Typography>
												</>
											) : (
												<Typography
													sx={{
														fontSize: 14,
														fontWeight: 600,
														color: dark,
														mb: 0.5,
													}}>
													{fileData.name}
												</Typography>
											)}
										</Box>
									)}
								</Dropzone>
								{txtError.status && (
									<Typography style={helperText}>
										{txtError.msg}
									</Typography>
								)}

								<Stack spacing={2} marginTop={2}>
									<Box>
										<Typography sx={labelStyle}>
											Template Name
										</Typography>

										<TextField
											fullWidth
											value={name}
											onChange={e =>
												setName(e.target.value)
											}
											size="small"
											placeholder="e.g. SenzCare Standard Care Plan v3"
											sx={inputStyle}
										/>
										{nameError.status && (
											<Typography style={helperText}>
												{nameError.msg}
											</Typography>
										)}
									</Box>

									<Box>
										<Typography sx={labelStyle}>
											Type
										</Typography>
										<TextField
											select
											fullWidth
											size="small"
											onChange={e => {
												setSummaryType(e.target.value);
											}}
											defaultValue="Care Plan"
											sx={inputStyle}>
											<MenuItem value="Care Plan">
												Care Plan
											</MenuItem>

											<MenuItem value="Summarizer">
												Summarizer
											</MenuItem>

											{/* <MenuItem value="CQC Report">
												CQC Report
											</MenuItem> */}
										</TextField>
									</Box>
									{summaryType === 'Summarizer' && (
										<Box>
											<Typography sx={labelStyle}>
												Summarizer Template Scope
											</Typography>
											<TextField
												select
												fullWidth
												size="small"
												onChange={e => {
													setSummaryTempScope(
														e.target.value,
													);
												}}
												defaultValue="Organization"
												sx={inputStyle}>
												<MenuItem value="Organization">
													Organization
												</MenuItem>
												<MenuItem value="Resident">
													Resident
												</MenuItem>
											</TextField>
										</Box>
									)}
									<Button
										onClick={onUploadSummaryTemplate}
										variant="contained"
										startIcon={<UploadFileOutlinedIcon />}
										sx={{
											width: '40%',
											borderRadius: '12px',
											backgroundColor: teal,
											textTransform: 'none',
											fontWeight: 600,
											fontSize: '13px',
											boxShadow: 'none',
											'&:hover': {
												backgroundColor: teal,
												boxShadow: 'none',
											},
											color: dark,
										}}>
										Upload & Activate
									</Button>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Box>
			<ToastBanner
				open={loading}
				message={'Uploading template...'}
				icon={<AutoAwesomeIcon color={'#FFFFFF'} />}
			/>
		</MainGrid>
	);
}
